import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Activity, XCircle, AlertCircle, Package } from 'lucide-react';
import { reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { formatMoney } from '@/utils/currency';

interface ShopStatusItem {
  item_code: string;
  item_name: string;
  quantity_sold: number;
  amount_sold: number;
}

interface ShopStatusResponse {
  high_selling: ShopStatusItem[];
  average_selling: ShopStatusItem[];
  dead: ShopStatusItem[];
  summary: {
    high_selling_count: number;
    average_selling_count: number;
    dead_count: number;
  };
}

export default function ShopStatusReportSection() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'high' | 'average' | 'dead'>('high');

  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['shopStatusReport', from_date, to_date],
    queryFn: async () => {
      const res = await reportsApi.getShopStatusReport({ from_date, to_date });
      return res as ShopStatusResponse;
    },
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p>Failed to load shop status report.</p>
      </div>
    );
  }

  const summary = data?.summary;
  
  const getTabItems = () => {
    if (!data) return [];
    if (activeTab === 'high') return data.high_selling;
    if (activeTab === 'average') return data.average_selling;
    return data.dead;
  };

  const items = getTabItems();

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-col lg:flex-row gap-4 justify-between lg:items-end">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 h-28" />
            ))}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 h-96" />
        </div>
      ) : summary ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('high')}
              className={`bg-white rounded-xl border shadow-sm p-5 relative overflow-hidden transition-all text-left ${
                activeTab === 'high' ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-gray-200 hover:border-emerald-300'
              }`}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('nav.highSelling', 'High Selling')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{summary.high_selling_count}</p>
                </div>
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
              </div>
              <p className="text-sm text-gray-500">Top 25% performers</p>
            </button>

            <button
              onClick={() => setActiveTab('average')}
              className={`bg-white rounded-xl border shadow-sm p-5 relative overflow-hidden transition-all text-left ${
                activeTab === 'average' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('nav.averageSelling', 'Average Selling')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{summary.average_selling_count}</p>
                </div>
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg shrink-0">
                  <Activity className="w-5 h-5" />
                </div>
              </div>
              <p className="text-sm text-gray-500">Standard sales volume</p>
            </button>

            <button
              onClick={() => setActiveTab('dead')}
              className={`bg-white rounded-xl border shadow-sm p-5 relative overflow-hidden transition-all text-left ${
                activeTab === 'dead' ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200 hover:border-red-300'
              }`}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-50 rounded-bl-full -mr-4 -mt-4 opacity-50 pointer-events-none" />
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('nav.deadProducts', 'Dead Products')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{summary.dead_count}</p>
                </div>
                <div className="p-2.5 bg-red-50 text-red-600 rounded-lg shrink-0">
                  <XCircle className="w-5 h-5" />
                </div>
              </div>
              <p className="text-sm text-gray-500">0 sales in this period</p>
            </button>
          </div>

          {/* Details Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col max-h-[600px]">
            <div className="p-4 border-b border-gray-100 shrink-0 bg-white flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">
                {activeTab === 'high' && 'High Selling Products'}
                {activeTab === 'average' && 'Average Selling Products'}
                {activeTab === 'dead' && 'Dead Products'}
              </h3>
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                {items.length} items
              </span>
            </div>
            
            <div className="overflow-y-auto flex-1 min-h-0">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-50 text-gray-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 font-medium bg-gray-50">Item Code</th>
                    <th className="px-4 py-3 font-medium bg-gray-50">Item Name</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Quantity Sold</th>
                    <th className="px-4 py-3 font-medium text-right bg-gray-50">Amount Sold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                          <Package className="w-8 h-8 text-gray-300 mb-2" />
                          <p>No products in this category</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map((item, idx) => (
                      <tr key={item.item_code || idx} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900">{item.item_code}</td>
                        <td className="px-4 py-3 text-gray-600">{item.item_name}</td>
                        <td className={`px-4 py-3 text-right font-medium ${activeTab === 'high' ? 'text-emerald-600' : 'text-gray-900'}`}>
                          {item.quantity_sold}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {formatMoney(item.amount_sold)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
